import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Row } from 'syngenta-digital-cropwise-react-ui-kit';
import { Formik, useFormikContext } from 'formik';

import CloseIcon from 'assets/close.svg';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import {
  FormValues,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import useProductCarrier from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/hooks/useProductCarrier';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import SelectCarrierStep from './components/SelectCarrierStep';
import CarrierDetailsStep from './components/CarrierDetailsStep';
import { generateInitialValues } from '../FillProductDetailsDrawer/helpers/generateInitialValues';
import { PRODUCT_DETAILS_SCHEMA } from '../FillProductDetailsDrawer/schemas';
import Footer from './components/Footer';

export interface EditCarrierDrawerProps {
  open: boolean;
  onClose: () => void;
}

const EditCarrierDrawer = ({ open, onClose }: EditCarrierDrawerProps) => {
  const { values: applicationValues, setFieldValue } = useFormikContext<
    FormValues
  >();

  const { updateUtilityState } = useAgOperationUtility();

  const { loadedCarrier, isLoading } = useProductCarrier();

  const carrierRowIndex = useMemo(
    () =>
      applicationValues.products.findIndex(
        (product: Product) => product.isCarrier
      ),
    [applicationValues]
  );

  const [step, setStep] = useState(0);
  const [originalCarrierId, setOriginalCarrierId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (open) {
      const carrier = applicationValues.products.find(
        (product: Product) => product.isCarrier
      );
      setOriginalCarrierId(carrier?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!loadedCarrier) {
      const carrier = applicationValues.products.find(
        (product: Product) => product.isCarrier
      );
      if (carrier) {
        updateUtilityState({
          selectedCarrier: carrier.id
        });
      }
    }
  }, [loadedCarrier, applicationValues.products, updateUtilityState]);

  const handleClose = (restoreCarrier = true) => {
    if (restoreCarrier && originalCarrierId) {
      updateUtilityState({ selectedCarrier: originalCarrierId });
    }
    setStep(0);
    onClose();
  };

  const handleBack = () => {
    if (step === 1) {
      setStep(0);
    } else {
      handleClose();
    }
  };

  const handleNext = async () => {
    setStep(1);
  };

  const handleSubmit = (values: Product) => {
    const updatedProducts = applicationValues.products.map((product, index) =>
      index === carrierRowIndex
        ? {
            ...product,
            id: values.id,
            name: values.name,
            priceUnit: values.priceUnit,
            priceValue: values.priceValue,
            saveAsNewListPrice: values.saveAsNewListPrice,
            totalCost: values.totalCost,
            appliedAreaValue: values.appliedAreaValue,
            coveragePercent: values.coveragePercent,
            applicationMethod: values.applicationMethod,
            targetPests: values.targetPests,
            density: values.density,
            stdfactor: values.stdfactor,
            manufacturer: values.manufacturer
          }
        : product
    );

    setFieldValue('products', updatedProducts);
    handleClose(false);
  };

  const mergedLoadedCarrier = () => {
    const carrierData = applicationValues.products[carrierRowIndex];
    if (!carrierData) return (loadedCarrier as unknown) as Product;

    // Only preserve the price from carrierData if it's the SAME carrier (IDs match)
    // When switching to a different carrier, we should use the new carrier's list price
    // Compare both IDs without dashes to ensure consistent format comparison
    const loadedCarrierId = (loadedCarrier as any)?.id?.replaceAll('-', '');
    const carrierDataId = carrierData.id?.replaceAll('-', '');
    const isSameCarrier = loadedCarrierId === carrierDataId;

    return {
      ...(loadedCarrier as any),
      ratePerAcre: carrierRowIndex >= 0 ? carrierData.ratePerAcre : undefined,
      ratePerAreaUnit:
        carrierRowIndex >= 0 ? carrierData.ratePerAreaUnit : undefined,
      appliedAreaValue: carrierData.appliedAreaValue,
      coveragePercent: carrierData.coveragePercent,
      applicationMethod: carrierData.applicationMethod,
      targetPests: carrierData.targetPests,
      isCarrier: true,
      isInUse: true,
      ...(isSameCarrier && {
        /* If price value is null/undefined it should fallback to loaded carrier average price,
           otherwise use a default price value of 0
        */
        priceValue:
          carrierData.priceValue ?? loadedCarrier?.averagePricePerUnit ?? 0,
        /* If price unit is not a string or is empty it should fallback to loaded carrier average price unit,
           otherwise use stdunit
        */
        priceUnit:
          typeof carrierData.priceUnit === 'string' &&
          carrierData.priceUnit !== ''
            ? carrierData.priceUnit
            : loadedCarrier?.averagePricePerUnitUnit ?? carrierData.stdunit
      })
    } as Product;
  };

  if (!loadedCarrier) return null;

  return (
    <Formik
      initialValues={generateInitialValues(
        applicationValues,
        mergedLoadedCarrier(),
        'none'
      )}
      validationSchema={PRODUCT_DETAILS_SCHEMA}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <Drawer
        placement="right"
        className="product-drawer"
        open={open}
        onClose={onClose}
        closable={false}
        maskClosable={false}
        width={520}
        footer={
          <Footer step={step} handleBack={handleBack} handleNext={handleNext} />
        }
      >
        <div className="h-full">
          <div className="pt-4 pr-4 pl-8">
            <Row justify="end">
              <button
                type="button"
                aria-label="Close"
                onClick={() => handleClose()}
              >
                <img alt="close" src={CloseIcon} />
              </button>
            </Row>
            {step === 1 && loadedCarrier && !isLoading ? (
              <div className="gap-4 mb-4" data-testid="product-container">
                <h1 className="text-xl font-semibold block">
                  {(loadedCarrier as any).name}
                </h1>
                <p className="text-normal text-neutral-60">
                  {(loadedCarrier as any).manufacturer}
                </p>
              </div>
            ) : (
              <div className="text-xl font-semibold pb-4">
                {LayoutStrings.products.tankInfo.selectCarrier}
              </div>
            )}
          </div>
          {step === 0 ? (
            <SelectCarrierStep />
          ) : (
            <CarrierDetailsStep
              carrier={mergedLoadedCarrier()}
              isLoading={isLoading}
            />
          )}
        </div>
      </Drawer>
    </Formik>
  );
};

export default EditCarrierDrawer;
